import { inject, Injectable } from '@angular/core';
import { TranslateTriggerService } from '@app/core/i18n/services/translate-trigger.service';
import { BrowserLocaleService } from '@app/core/i18n/services/browser-locale.service';
import { LangSupport } from '@app/core/i18n/enums/lang-support.enum';

export interface CsvColumnConfig<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class CsvExportService {
  private translateTrigger = inject(TranslateTriggerService);
  private browserLocaleService = inject(BrowserLocaleService);

  public downloadCsv<T>(
    filename: `${string}.csv`,
    data: T[],
    columns: CsvColumnConfig<T>[],
    customDelimiter?: string
  ): void {
    const csvContent = this.generateCsv(data, columns, customDelimiter);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.click();

    URL.revokeObjectURL(url);
  }

  private generateCsv<T>(data: T[], columns: CsvColumnConfig<T>[], customDelimiter?: string): string {
    const lang = this.translateTrigger.getCurrentLang();
    const region = this.browserLocaleService.getRegionByLang(lang as LangSupport);
    const appLocale = `${lang}-${region}`;
    const appDelimiter = new Intl.NumberFormat(appLocale).format(1.1).includes(',') ? ';' : ',';
    const delimiter = customDelimiter || appDelimiter;

    if (!data || data.length === 0 || !columns || columns.length === 0) {
      return '';
    }

    const headers = columns.map(c => this.escapeCsvValue(c.header, delimiter)).join(delimiter);

    const rows = data.map(row => columns.map(c => this.escapeCsvValue(c.value(row), delimiter)).join(delimiter));

    return [headers, ...rows].join('\n');
  }

  private escapeCsvValue(value: unknown, delimiter: string): string {
    if (value === null || value === undefined) return '';
    let str = String(value);

    const needsEscaping = str.includes(delimiter) || str.includes('"') || str.includes('\n');
    if (needsEscaping) {
      str = '"' + str.replace(/"/g, '""') + '"';
    }

    return str;
  }
}
